import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { Spell } from '../../types';
import { SpellCard } from '../shared/SpellCard';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';

const TOTAL_PAGES = 8;

const PAGE_LABELS = [
  'School Spell 1 of 3',
  'School Spell 2 of 3',
  'School Spell 3 of 3',
  'Aligned Spell 1 of 3',
  'Aligned Spell 2 of 3',
  'Aligned Spell 3 of 3',
  'Neutral Spell 1 of 2',
  'Neutral Spell 2 of 2',
];

interface NeutralGroup {
  school: string;
  spells: Spell[];
}

interface AlignedGroup {
  school: string;
  spells: Spell[];
}

export const SelectStartingSpellsModal: React.FC = () => {
  const {
    ui,
    selectedWarbandId,
    spellcastersAndSpells,
    closeAddStartingSpellsModal,
  } = useWarbandStore();

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const rawPrimarySpells = useWarbandStore((s) => s.primarySpells);
  const rawAlignedSpells = useWarbandStore((s) => s.alignedSpells);
  const rawNeutralSpells = useWarbandStore((s) => s.neutralSpells);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [selections, setSelections] = React.useState<(string | null)[]>(
    Array(TOTAL_PAGES).fill(null)
  );
  const [neutralSchoolFilter, setNeutralSchoolFilter] = React.useState<[string, string]>(['', '']);

  const primarySpells = React.useMemo(() => {
    const existingIds = new Set(
      (spellcastersAndSpells?.warbandSpells || []).map((spell) => spell.id)
    );
    return rawPrimarySpells.filter((s) => !existingIds.has(s.id));
  }, [rawPrimarySpells, spellcastersAndSpells]);

  const alignedGrouped = React.useMemo((): AlignedGroup[] => {
    const existingIds = new Set(
      (spellcastersAndSpells?.warbandSpells || []).map((spell) => spell.id)
    );
    const flat = rawAlignedSpells.flat().filter((s) => !existingIds.has(s.id));
    const map = new Map<string, Spell[]>();
    flat.forEach((spell) => {
      const school = spell.school?.trim() || 'Unknown';
      if (!map.has(school)) map.set(school, []);
      map.get(school)!.push(spell);
    });
    return Array.from(map.entries())
      .map(([school, spells]) => ({ school, spells: [...spells].sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.school.localeCompare(b.school));
  }, [rawAlignedSpells, spellcastersAndSpells]);

  const neutralGrouped = React.useMemo((): NeutralGroup[] => {
    const existingIds = new Set(
      (spellcastersAndSpells?.warbandSpells || []).map((spell) => spell.id)
    );
    const flat = rawNeutralSpells.flat().filter((s) => !existingIds.has(s.id));
    const map = new Map<string, Spell[]>();
    flat.forEach((spell) => {
      const school = spell.school?.trim() || 'Unknown';
      if (!map.has(school)) map.set(school, []);
      map.get(school)!.push(spell);
    });
    return Array.from(map.entries())
      .map(([school, spells]) => ({ school, spells: [...spells].sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.school.localeCompare(b.school));
  }, [rawNeutralSpells, spellcastersAndSpells]);

  // Reset state when modal opens
  React.useEffect(() => {
    if (!ui.addStartingSpellsModalOpen) return;
    setCurrentPage(0);
    setSelections(Array(TOTAL_PAGES).fill(null));
    const defaultSchool = neutralGrouped[0]?.school ?? '';
    setNeutralSchoolFilter([defaultSchool, defaultSchool]);
    setError(null);
  }, [ui.addStartingSpellsModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // All spells flat (for SpellCard lookup)
  const allSpells = React.useMemo(
    () => [...primarySpells, ...alignedGrouped.flatMap((g) => g.spells), ...neutralGrouped.flatMap((g) => g.spells)],
    [primarySpells, alignedGrouped, neutralGrouped]
  );

  // Spells already picked in earlier pages of this session
  const sessionPickedIds = React.useMemo(
    () =>
      new Set(
        selections
          .filter((id, idx) => id !== null && idx !== currentPage)
          .map((id) => id as string)
      ),
    [selections, currentPage]
  );

  const neutralSchools = React.useMemo(
    () => neutralGrouped.map((g) => g.school),
    [neutralGrouped]
  );

  const neutralSpellSchoolById = React.useMemo(() => {
    const schoolBySpellId = new Map<string, string>();
    neutralGrouped.forEach((group) => {
      group.spells.forEach((spell) => {
        schoolBySpellId.set(spell.id, group.school);
      });
    });
    return schoolBySpellId;
  }, [neutralGrouped]);

  const neutralSelectedSchools = React.useMemo<[string | null, string | null]>(() => {
    const firstNeutralSelection = selections[6];
    const secondNeutralSelection = selections[7];

    return [
      firstNeutralSelection ? neutralSpellSchoolById.get(firstNeutralSelection) ?? null : null,
      secondNeutralSelection ? neutralSpellSchoolById.get(secondNeutralSelection) ?? null : null,
    ];
  }, [selections, neutralSpellSchoolById]);

  const neutralAvailableSchools = React.useMemo<[string[], string[]]>(() => {
    const selectedSchoolOnNeutral1 = neutralSelectedSchools[0];
    const selectedSchoolOnNeutral2 = neutralSelectedSchools[1];

    const neutral1Schools = neutralSchools.filter(
      (school) => !selectedSchoolOnNeutral2 || school !== selectedSchoolOnNeutral2
    );
    const neutral2Schools = neutralSchools.filter(
      (school) => !selectedSchoolOnNeutral1 || school !== selectedSchoolOnNeutral1
    );

    return [neutral1Schools, neutral2Schools];
  }, [neutralSchools, neutralSelectedSchools]);

  const isNeutralPage = currentPage >= 6;
  const neutralPageIndex = currentPage - 6; // 0 or 1
  const neutralSchoolsForCurrentPage = isNeutralPage
    ? neutralAvailableSchools[neutralPageIndex]
    : [];
  const isSchoolPage = currentPage <= 2;
  const schoolPageSchool = primarySpells[0]?.school?.trim() || '';
  const isAlignedPage = currentPage >= 3 && currentPage <= 5;
  const alignedPageIndex = currentPage - 3; // 0, 1, 2
  const alignedPageSchool = alignedGrouped[alignedPageIndex]?.school ?? '';

  React.useEffect(() => {
    if (neutralSchools.length === 0) {
      return;
    }

    let filtersChanged = false;
    let selectionsChanged = false;

    const nextFilters: [string, string] = [neutralSchoolFilter[0], neutralSchoolFilter[1]];
    const nextSelections = [...selections];

    ([0, 1] as const).forEach((pageIndex) => {
      const availableSchools = neutralAvailableSchools[pageIndex];
      const currentFilter = nextFilters[pageIndex];

      if (!availableSchools.includes(currentFilter)) {
        nextFilters[pageIndex] = availableSchools[0] ?? '';
        filtersChanged = true;
      }

      const selectionIndex = 6 + pageIndex;
      const selectedSpellId = nextSelections[selectionIndex];
      if (!selectedSpellId) {
        return;
      }

      const selectedSchool = neutralSpellSchoolById.get(selectedSpellId);
      if (!selectedSchool || !availableSchools.includes(selectedSchool)) {
        nextSelections[selectionIndex] = null;
        selectionsChanged = true;
      }
    });

    if (filtersChanged) {
      setNeutralSchoolFilter(nextFilters);
    }

    if (selectionsChanged) {
      setSelections(nextSelections);
    }
  }, [
    neutralAvailableSchools,
    neutralSchoolFilter,
    neutralSchools,
    neutralSpellSchoolById,
    selections,
  ]);

  const pageSpells = React.useMemo((): Spell[] => {
    if (currentPage <= 2) {
      return primarySpells.filter((s) => !sessionPickedIds.has(s.id));
    } else if (currentPage <= 5) {
      const alignedGroup = alignedGrouped[alignedPageIndex];
      return (alignedGroup?.spells ?? []).filter((s) => !sessionPickedIds.has(s.id));
    } else {
      const school = neutralSchoolFilter[neutralPageIndex];
      const group = neutralGrouped.find((g) => g.school === school);
      return (group?.spells ?? []).filter((s) => !sessionPickedIds.has(s.id));
    }
  }, [
    currentPage,
    primarySpells,
    alignedGrouped,
    alignedPageIndex,
    neutralGrouped,
    neutralSchoolFilter,
    neutralPageIndex,
    sessionPickedIds,
  ]);

  const currentSelectionId = selections[currentPage] ?? '';

  const selectedSpell = React.useMemo(
    () => allSpells.find((s) => s.id === currentSelectionId) ?? null,
    [allSpells, currentSelectionId]
  );

  const setCurrentNeutralSchool = (school: string) => {
    setNeutralSchoolFilter((prev) => {
      const next: [string, string] = [prev[0], prev[1]];
      next[neutralPageIndex] = school;
      return next;
    });

    // Clear this page's selection if it no longer belongs to the new school
    const group = neutralGrouped.find((g) => g.school === school);
    const spellIds = new Set((group?.spells ?? []).map((s) => s.id));
    if (currentSelectionId && !spellIds.has(currentSelectionId)) {
      setSelections((prev) => {
        const next = [...prev];
        next[currentPage] = null;
        return next;
      });
    }
  };

  const handleSelectSpell = (spellId: string) => {
    setSelections((prev) => {
      const next = [...prev];
      next[currentPage] = spellId;
      return next;
    });
  };

  const handleNext = () => {
    if (currentPage < TOTAL_PAGES - 1) setCurrentPage((p) => p + 1);
  };

  const handleBack = () => {
    if (currentPage > 0) setCurrentPage((p) => p - 1);
  };

  const handleFinish = async () => {
    if (!selectedWarbandId || saving) return;

    const spellIds = selections.filter(Boolean) as string[];
    setSaving(true);
    setError(null);

    try {
      const selectedSpells = [...primarySpells, ...alignedGrouped.flatMap((group) => group.spells), ...neutralGrouped.flatMap((group) => group.spells)]
        .filter((spell, index, allSpells) =>
          spellIds.includes(spell.id) && allSpells.findIndex((candidate) => candidate.id === spell.id) === index
        );

      useDirtyTrackingStore.getState().queueSpellAdds(selectedSpells);
      handleClose();
    } catch {
      setError('Failed to save spells. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setCurrentPage(0);
    setSelections(Array(TOTAL_PAGES).fill(null));
    setNeutralSchoolFilter(['', '']);
    setError(null);
    closeAddStartingSpellsModal();
  };

  const isLastPage = currentPage === TOTAL_PAGES - 1;
  const canAdvance = !!selections[currentPage];

  return (
    <Dialog open={ui.addStartingSpellsModalOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Starting Spells — {PAGE_LABELS[currentPage]}</DialogTitle>

      <LinearProgress
        variant="determinate"
        value={(currentPage / TOTAL_PAGES) * 100}
        sx={{ mx: 3, mb: 1, borderRadius: 1 }}
      />

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minHeight: 420 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        {!selectedWarbandId ? (
          <Typography variant="body2" color="text.secondary">
            No party selected.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {isSchoolPage && (
              <SpellSchoolChip label={schoolPageSchool} />
            )}

            {isAlignedPage && (
              <SpellSchoolChip label={alignedPageSchool} />
            )}

            {isNeutralPage && neutralSchoolsForCurrentPage.length > 0 && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id={`neutral-school-label-${neutralPageIndex}`}>School</InputLabel>
                <Select
                  labelId={`neutral-school-label-${neutralPageIndex}`}
                  value={neutralSchoolFilter[neutralPageIndex]}
                  label="School"
                  onChange={(e) => setCurrentNeutralSchool(e.target.value)}
                >
                  {neutralSchoolsForCurrentPage.map((school) => (
                    <MenuItem key={school} value={school}>
                      <SpellSchoolChip label={school} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {pageSpells.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No spells available for this selection.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pageSpells.map((spell) => (
                  <Chip
                    key={spell.id}
                    label={spell.name}
                    clickable
                    variant={currentSelectionId === spell.id ? 'filled' : 'outlined'}
                    color={currentSelectionId === spell.id ? 'primary' : 'default'}
                    onClick={() => handleSelectSpell(spell.id)}
                  />
                ))}
              </Box>
            )}

            {selectedSpell ? (
              <SpellCard spell={selectedSpell} />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2, height: 250 }}
              >
                Select a spell to view details
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleBack} disabled={currentPage === 0 || saving}>
          Back
        </Button>
        {isLastPage ? (
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={!canAdvance || saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {saving ? 'Saving...' : 'Finish'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} disabled={!canAdvance}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
